import React from "react";
import { LayoutTemplate, SearchX, Plus, MousePointerClick, Sparkles, Smartphone } from "lucide-react";
import { EmptyState as SharedEmptyState } from "components/ui/empty-state";

/**
 * Adapter over the shared `EmptyState`.
 *
 * The original was ~120 lines of bespoke markup with inline SVG paths and
 * light-only colours (`bg-white`, `text-gray-900`, `border-gray-200`), so it
 * rendered as a white slab in dark mode. It also drew a "Browse template
 * gallery" button that had no click handler.
 *
 * Both states — nothing-yet and filtered-to-nothing — now come from the shared
 * component, which puts the right action first in each case: create when the
 * library is empty, clear filters when it is not.
 */
interface EmptyStateProps {
    hasFilters: boolean;
    onCreateNew: () => void;
    onClearFilters: () => void;
}

const EmptyState: React.FC<EmptyStateProps> = ({ hasFilters, onCreateNew, onClearFilters }) => {
    if (hasFilters) {
        return (
            <SharedEmptyState
                variant="no-results"
                icon={SearchX}
                title="No templates match these filters"
                description="Nothing in your library fits the current search and filters. Widen them, or start a new template from scratch."
                primaryAction={{ label: "Clear all filters", onClick: onClearFilters }}
                secondaryAction={{
                    label: "Create template",
                    onClick: onCreateNew,
                    icon: <Plus className="w-4 h-4" />,
                }}
            />
        );
    }

    return (
        <SharedEmptyState
            variant="empty"
            icon={LayoutTemplate}
            size="lg"
            title="Build your first email template"
            description="Templates are reusable designs you drop into any campaign. Start from a blank canvas or pick one of the ready-made layouts."
            primaryAction={{
                label: "Create your first template",
                onClick: onCreateNew,
                icon: <Plus className="w-4 h-4" />,
            }}
            hints={[
                {
                    icon: MousePointerClick,
                    title: "Drag & drop editor",
                    description: "Build emails visually — no HTML required.",
                },
                {
                    icon: Sparkles,
                    title: "AI-assisted content",
                    description: "Generate copy and subject lines, then refine them.",
                },
                {
                    icon: Smartphone,
                    title: "Responsive by default",
                    description: "Every template adapts to phone and desktop inboxes.",
                },
            ]}
        />
    );
};

export default EmptyState;
